import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Tickers from "../components/Tickers";
import Graph from "../components/Graph";
import { validateTickers } from "../utils/validateTickers";
import { ToastContainer, toast, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Main = () => {
	const [graphData, setGraphData] = useState(null); // State for graph data
	const [loadingGraph, setLoadingGraph] = useState(false); // State for loading graph

	const notifyError = (msg) =>
		toast.error("Error: " + msg, {
			position: "top-center",
			autoClose: 2000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: "light",
			transition: Slide,
		}); // Function to notify error

	const notifyWarn = (msg) =>
		toast.warn("Warning: " + msg, {
			position: "top-center",
			autoClose: 2000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: "light",
			transition: Slide,
		});

	const generateGraph = async (tickers) => {
		if (
			tickers.length === 0 ||
			tickers.some((ticker) => ticker.symbol === "")
		) {
			return;
		}
		const validationRes = validateTickers(tickers);
		if (validationRes !== true) {
			if (validationRes.type === "error") {
				notifyError(validationRes.message);
				return;
			} else if (validationRes.type === "warning")
				notifyWarn(validationRes.message);
		}
		setLoadingGraph(true); // Set loading state
		const apiUrl = process.env.REACT_APP_API_URL;
		const API_KEY = process.env.REACT_APP_API_KEY;

		const currentDate = new Date();
		const startDate = new Date(
			currentDate.getFullYear() - 5,
			currentDate.getMonth(),
			currentDate.getDate()
		);
		const formattedStartDate = startDate.toISOString().split("T")[0];
		const formattedEndDate = currentDate.toISOString().split("T")[0];

		const requestBody = {
			symbols: tickers,
			start: formattedStartDate,
			end: formattedEndDate,
		};
		console.log("Request:", requestBody);

		try {
			const response = await fetch(`${apiUrl}/process`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					api_key: API_KEY,
				},
				body: JSON.stringify(requestBody),
			});
			if (!response.ok) {
				const errorRes = await response.json();
				console.error("Error generating graph:", errorRes);
				notifyError(errorRes.detail);
				setLoadingGraph(false); // Reset loading state
				return;
			}
			const data = await response.json();
			console.log("Graph data:", data);
			setGraphData(data); // Update graph data
			setLoadingGraph(false); // Reset loading state
		} catch (error) {
			console.error("Error generating graph:", error);
			setLoadingGraph(false); // Reset loading state
		}
	};

	const [enableGenerateCSV, setEnableGenerateCSV] = useState(false);
	useEffect(() => {
		if (graphData) {
			setEnableGenerateCSV(true);
		} else {
			setEnableGenerateCSV(false);
		}
	}, [graphData]);

	function generateCSV() {
		if (!graphData) return;

		console.log(graphData);

		const headers = ["Symbols", "Sharpe", "Volatility", "Returns"];
		let csvData = [];

		for (let i in graphData.chart_data) {
			const riskFreeRate = 0.02;
			const sharpeRatio =
				(graphData.opt_rets[i] - riskFreeRate) /
				graphData.optimal_vol[i];

			csvData.push({
				label: graphData.chart_data[i].label,
				sharpeRatio: sharpeRatio,
				optimal_vol: graphData.optimal_vol[i],
				opt_ret: graphData.opt_rets[i],
			});
		}

		csvData = csvData.sort((a, b) =>
			a.sharpeRatio < b.sharpeRatio
				? 1
				: b.sharpeRatio < a.sharpeRatio
				? -1
				: 0
		);

		let rows = csvData.map((data) => [
			data.label || "",
			data.sharpeRatio || "",
			data.optimal_vol || "",
			data.opt_ret || "",
		]);

		const csv = rows.map((row) => row.join(",")).join("\r\n");

		const blob = new Blob([headers.join(",") + "\r\n" + csv], {
			type: "text/csv",
		});
		const url = URL.createObjectURL(blob);

		const now = new Date();
		const formattedDate = now.toISOString().replace(/[:.]/g, "-");

		const a = document.createElement("a");
		a.href = url;
		a.download = `efficient_frontier_${formattedDate}.csv`;
		a.click();
	}

	return (
		<div className="flex flex-col min-h-screen">
			<ToastContainer
				position="top-center"
				autoClose={2000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="light"
				transition={Slide}
			/>
			<Header />
			<div className="flex flex-col-reverse lg:flex-row">
				<div className="min-w-[30vw] lg:min-h-[calc(100vh-6rem)] border-r">
					<Tickers generateGraph={generateGraph} />
				</div>
				<div className="flex flex-col-reverse md:flex-col shrink h-full mx-4 pt-2 md:pt-0">
					<button
						disabled={!enableGenerateCSV}
						onClick={generateCSV}
						className={`md:ml-14 md:mt-4 mx-2 px-4 py-2 md:w-fit h-fit items-center justify-center md:justify-normal flex gap-2 rounded text-sm font-medium transition-all ${
							enableGenerateCSV
								? "bg-green-500 hover:bg-green-600 text-white active:scale-95"
								: "bg-gray-400 text-white"
						}`}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="32"
							height="32"
							viewBox="0 0 24 24"
							className="fill-current text-white w-6 h-6"
						>
							<path d="M13 12h3l-4 4l-4-4h3V8h2zm2-8H5v16h14V8h-4zM3 2.992C3 2.444 3.447 2 3.999 2H16l5 5v13.993A1 1 0 0 1 20.007 22H3.993A1 1 0 0 1 3 21.008z" />
						</svg>{" "}
						Download CSV
					</button>
					<Graph graphData={graphData} loadingGraph={loadingGraph} />
				</div>
			</div>
		</div>
	);
};

export default Main;
